import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import api from "../lib/api";
import { formatIDR } from "../lib/format";
import { toast } from "sonner";
import {
  Zap, Search, RefreshCw, Save, Plug, Download, Trash2, RotateCw,
  CheckCircle2, AlertTriangle, Clock, Send, ImageIcon, Pencil, X,
} from "lucide-react";


/**
 * Panel Admin: Produk Instan (integrasi undeadshopp.com)
 * -----------------------------------------------------
 * 3 sub-tab:
 *   1. Koneksi  → simpan base_url / API key / markup / webhook secret + tes koneksi
 *   2. Katalog  → tarik katalog remote, pilih, import jadi produk admin
 *   3. Produk   → produk instan yang sudah diimport (edit harga/markup/aktif)
 *   4. Antrean  → job pengiriman otomatis (retry / kirim manual)
 */

const SUB_TABS = [
  { id: "connection", label: "Koneksi", icon: Plug },
  { id: "catalog", label: "Katalog API", icon: Download },
  { id: "products", label: "Produk Instan", icon: Zap },
  { id: "jobs", label: "Antrean Kirim", icon: Send },
];

const JOB_BADGE = {
  success: { label: "Terkirim", cls: "bg-emerald-500/15 text-emerald-400", Icon: CheckCircle2 },
  pending: { label: "Diproses", cls: "bg-[#FFD700]/15 text-[#FFD700]", Icon: Clock },
  processing: { label: "Diproses", cls: "bg-[#FFD700]/15 text-[#FFD700]", Icon: Clock },
  failed: { label: "Gagal", cls: "bg-[#FF003C]/15 text-[#FF003C]", Icon: AlertTriangle },
};

const inputCls =
  "w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white focus:border-[#00F0FF] outline-none";
const labelCls = "block text-[10px] uppercase tracking-widest text-white/50 mb-1";
const btnPrimary =
  "inline-flex items-center gap-2 px-4 py-2 rounded bg-[#FF003C] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#ff2a5c] disabled:opacity-50";
const btnGhost =
  "inline-flex items-center gap-2 px-3 py-2 rounded border border-white/15 text-white/70 text-xs font-bold uppercase tracking-widest hover:bg-white/5 disabled:opacity-50";

function Card({ title, desc, children }) {
  return (
    <div className="bg-[#0b0b0f] border border-white/10 rounded-lg p-4 mb-4">
      {title && (
        <div className="mb-3">
          <h3 className="text-sm font-bold text-white uppercase tracking-widest">{title}</h3>
          {desc && <p className="text-xs text-white/40 mt-1">{desc}</p>}
        </div>
      )}
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 1. Koneksi                                                          */
/* ------------------------------------------------------------------ */
function ConnectionTab({ onSaved }) {
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState({});
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/integrations/undeadshopp");
      setCfg(data.config);
      setStats(data.stats || {});
      setWebhookUrl(data.webhook_url || "");
    } catch (e) {
      toast.error("Gagal memuat konfigurasi integrasi");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      // Jangan kirim balik nilai yang masih ter-mask (******abcd)
      ["api_key", "api_secret", "webhook_secret"].forEach((f) => {
        if (typeof payload[f] === "string" && payload[f].startsWith("******")) delete payload[f];
      });
      delete payload.id;
      const { data } = await api.put("/admin/integrations/undeadshopp", payload);
      setCfg(data.config);
      toast.success("Konfigurasi tersimpan");
      onSaved?.(data.config);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      await api.post("/admin/integrations/undeadshopp/test");
      toast.success("Koneksi ke undeadshopp.com berhasil");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Koneksi gagal");
    } finally {
      setTesting(false);
    }
  };

  if (!cfg) return <p className="text-white/40 text-sm">Memuat…</p>;

  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { label: "Produk Instan", value: stats.products ?? 0 },
          { label: "Antrean Pending", value: stats.pending_jobs ?? 0 },
          { label: "Gagal Kirim", value: stats.failed_jobs ?? 0 },
        ].map((s) => (
          <div key={s.label} className="bg-black/40 border border-white/10 rounded p-3">
            <p className="text-[10px] uppercase tracking-widest text-white/40">{s.label}</p>
            <p className="text-xl font-bold text-[#00F0FF]">{s.value}</p>
          </div>
        ))}
      </div>

      <Card title="Kredensial API" desc="Data ini dipakai server saat meneruskan pesanan ke undeadshopp.com.">
        <div className="flex items-center gap-2 mb-4">
          <button
            onClick={() => set("enabled", !cfg.enabled)}
            className={`px-3 py-1.5 rounded text-xs font-bold uppercase tracking-widest ${
              cfg.enabled ? "bg-emerald-500/20 text-emerald-400" : "bg-white/10 text-white/50"
            }`}
          >
            {cfg.enabled ? "Integrasi Aktif" : "Integrasi Nonaktif"}
          </button>
          <span className="text-xs text-white/40">
            Nonaktif = pesanan produk instan masuk antrean, tidak dikirim ke API.
          </span>
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className={labelCls}>Base URL API</label>
            <div className="flex gap-2">
              <input className={inputCls} value={cfg.base_url || ""}
                     onChange={(e) => set("base_url", e.target.value)}
                     placeholder="https://api.undeadshopp.com/v2" />
              {/* Tombol bantu: sekali klik langsung set preset resmi API v2
                  undeadshopp (base URL + mode auth + nama header). */}
              <button
                type="button"
                data-testid="preset-undead-v2"
                onClick={() => {
                  set("base_url", "https://api.undeadshopp.com/v2");
                  set("auth_mode", "authorization");
                  set("api_key_header", "Authorization");
                }}
                className="shrink-0 px-3 border border-[#00F0FF]/40 text-[#00F0FF] text-[10px] uppercase tracking-widest hover:bg-[#00F0FF]/10"
                title="Isi otomatis sesuai dokumentasi undeadshopp API v2"
              >
                Preset v2
              </button>
            </div>
            <p className="text-[11px] text-white/40 mt-1">
              Sesuai dokumentasi API v2: <span className="text-[#00F0FF] font-mono">https://api.undeadshopp.com/v2</span>
            </p>
          </div>
          <div>
            <label className={labelCls}>Mode Autentikasi</label>
            <select className={inputCls} value={cfg.auth_mode || "authorization"}
                    onChange={(e) => set("auth_mode", e.target.value)}>
              <option value="authorization">Authorization (token polos) — undeadshopp v2</option>
              <option value="bearer">Bearer Token</option>
              <option value="api_key_header">API Key Header</option>
              <option value="query">Query Param (?api_key=)</option>
              <option value="hmac">HMAC Signature</option>
            </select>
            <p className="text-[11px] text-white/40 mt-1">
              undeadshopp v2 mengirim key tanpa prefix "Bearer".
            </p>
          </div>
          <div>
            <label className={labelCls}>Nama Header API Key</label>
            <input className={inputCls} value={cfg.api_key_header || ""}
                   onChange={(e) => set("api_key_header", e.target.value)} placeholder="Authorization" />
            <p className="text-[11px] text-white/40 mt-1">Untuk undeadshopp v2 isi: Authorization</p>
          </div>
          <div>
            <label className={labelCls}>API Key</label>
            <input className={inputCls} value={cfg.api_key || ""}
                   onChange={(e) => set("api_key", e.target.value)} placeholder="masukkan API key" />
            <p className="text-[11px] text-white/40 mt-1">
              Ambil di undeadshopp.com → menu API / Developer.
            </p>
          </div>
          <div>
            <label className={labelCls}>API Secret (HMAC)</label>
            <input className={inputCls} value={cfg.api_secret || ""}
                   onChange={(e) => set("api_secret", e.target.value)} placeholder="kosongkan bila tidak diberi" />

          </div>
        </div>
      </Card>

      <Card title="Harga Jual Otomatis" desc="Harga jual = harga modal API + markup, lalu dibulatkan ke atas.">
        <div className="grid md:grid-cols-4 gap-3">
          <div>
            <label className={labelCls}>Tipe Markup</label>
            <select className={inputCls} value={cfg.markup_type || "percentage"}
                    onChange={(e) => set("markup_type", e.target.value)}>
              <option value="percentage">Persen (%)</option>
              <option value="fixed">Nominal (Rp)</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Nilai Markup</label>
            <input type="number" className={inputCls} value={cfg.markup_value ?? 0}
                   onChange={(e) => set("markup_value", Number(e.target.value))} />
          </div>
          <div>
            <label className={labelCls}>Pembulatan (Rp)</label>
            <input type="number" className={inputCls} value={cfg.round_to ?? 100}
                   onChange={(e) => set("round_to", Number(e.target.value))} />
          </div>
          <div className="flex items-end">
            <button onClick={() => set("auto_sync_price", !cfg.auto_sync_price)}
                    className={`w-full px-3 py-2 rounded text-xs font-bold uppercase tracking-widest ${
                      cfg.auto_sync_price ? "bg-emerald-500/20 text-emerald-400" : "bg-white/10 text-white/50"
                    }`}>
              Auto Sync Harga
            </button>
          </div>
        </div>
      </Card>

      <Card title="Pengiriman & Webhook"
            desc="Bila API membalas asinkron, undeadshopp.com dapat menembak webhook di bawah ini.">
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <label className={labelCls}>Timeout (detik)</label>
            <input type="number" className={inputCls} value={cfg.timeout ?? 25}
                   onChange={(e) => set("timeout", Number(e.target.value))} />
          </div>
          <div>
            <label className={labelCls}>Maks. Percobaan Otomatis</label>
            <input type="number" className={inputCls} value={cfg.max_attempts ?? 4}
                   onChange={(e) => set("max_attempts", Number(e.target.value))} />
          </div>
          <div>
            <label className={labelCls}>Webhook Secret (HMAC SHA256)</label>
            <input className={inputCls} value={cfg.webhook_secret || ""}
                   onChange={(e) => set("webhook_secret", e.target.value)} placeholder="opsional tapi disarankan" />
          </div>
          <div className="md:col-span-3">
            <label className={labelCls}>URL Webhook (berikan ke undeadshopp.com)</label>
            <input className={`${inputCls} text-[#00F0FF]`} readOnly value={webhookUrl} />
          </div>
        </div>
      </Card>

      <div className="flex gap-2">
        <button className={btnPrimary} onClick={save} disabled={saving}>
          <Save className="w-4 h-4" /> {saving ? "Menyimpan…" : "Simpan"}
        </button>
        <button className={btnGhost} onClick={test} disabled={testing}>
          <Plug className="w-4 h-4" /> {testing ? "Menguji…" : "Tes Koneksi"}
        </button>
        <button className={btnGhost} onClick={load}>
          <RefreshCw className="w-4 h-4" /> Muat Ulang
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 2. Katalog remote                                                   */
/* ------------------------------------------------------------------ */
function CatalogTab() {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [warning, setWarning] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/integrations/undeadshopp/catalog", {
        params: { q: q || undefined, limit: 200 },
      });
      setItems(data.items || []);
      setSelected({});
      setWarning(data.warning || "");
      if (data.warning) toast.error("Katalog undeadshopp sedang bermasalah");
      else if (!data.items?.length) toast.info("Katalog kosong / filter tidak cocok");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal mengambil katalog";
      setWarning(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const toggle = (it) =>
    setSelected((s) => {
      const n = { ...s };
      if (n[it.external_id]) delete n[it.external_id];
      else n[it.external_id] = { ...it, price: it.suggested_price };
      return n;
    });

  const selectedList = useMemo(() => Object.values(selected), [selected]);

  const doImport = async () => {
    if (!selectedList.length) return toast.error("Pilih minimal satu produk");
    setImporting(true);
    try {
      const payload = {
        items: selectedList.map((it) => ({
          external_id: it.external_id,
          title: it.title,
          description: it.description,
          cost_price: it.cost_price,
          price: Number(it.price) || undefined,
          image_url: it.image_url,
          stock: it.stock ?? undefined,
        })),
      };
      const { data } = await api.post("/admin/integrations/undeadshopp/import", payload);
      toast.success(`Import selesai: ${data.created.length} baru, ${data.updated.length} diperbarui`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal import");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input className={`${inputCls} pl-9`} placeholder="Cari produk di undeadshopp.com…"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        <button className={btnGhost} onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Tarik Katalog
        </button>
        <button className={btnPrimary} onClick={doImport} disabled={importing || !selectedList.length}>
          <Download className="w-4 h-4" /> Import ({selectedList.length})
        </button>
      </div>

      {warning && (
        <div className="mb-4 flex gap-3 rounded border border-[#FFD700]/30 bg-[#FFD700]/10 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#FFD700]" />
          <div className="text-xs leading-relaxed text-[#FFD700]/90">
            <p className="font-bold uppercase tracking-widest">Katalog gagal ditarik</p>
            <p className="mt-1 text-[#FFD700]/70">{warning}</p>
            <p className="mt-1 text-white/40">
              Koneksi &amp; API key kamu tetap valid (cek di tab “Koneksi” → Tes Koneksi).
              Sementara ini kamu masih bisa menambah produk instan manual di tab
              “Produk Instan” dengan mengisi External ID layanan undeadshopp.
            </p>
          </div>
        </div>
      )}

      <div className="border border-white/10 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-white/50 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="p-2 w-8"></th>
              <th className="p-2 text-left">Produk</th>
              <th className="p-2 text-left">SKU</th>
              <th className="p-2 text-right">Modal</th>
              <th className="p-2 text-right">Harga Jual</th>
              <th className="p-2 text-right">Stok</th>
              <th className="p-2 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const sel = selected[it.external_id];
              return (
                <tr key={it.external_id} className="border-t border-white/5 hover:bg-white/5">
                  <td className="p-2 text-center">
                    <input type="checkbox" checked={!!sel} onChange={() => toggle(it)} />
                  </td>
                  <td className="p-2 text-white">{it.title}</td>
                  <td className="p-2 text-white/40 font-mono text-xs">{it.external_sku}</td>
                  <td className="p-2 text-right text-white/60">{formatIDR(it.cost_price)}</td>
                  <td className="p-2 text-right">
                    {sel ? (
                      <input type="number" className="w-28 bg-black/40 border border-white/10 rounded px-2 py-1 text-right text-[#00F0FF]"
                             value={sel.price}
                             onChange={(e) =>
                               setSelected((s) => ({ ...s, [it.external_id]: { ...sel, price: e.target.value } }))} />
                    ) : (
                      <span className="text-[#00F0FF]">{formatIDR(it.suggested_price)}</span>
                    )}
                  </td>
                  <td className="p-2 text-right text-white/60">{it.stock ?? "∞"}</td>
                  <td className="p-2 text-center">
                    {it.imported_product_id ? (
                      <span className="text-[10px] px-2 py-1 rounded bg-emerald-500/15 text-emerald-400">Sudah diimport</span>
                    ) : (
                      <span className="text-[10px] px-2 py-1 rounded bg-white/10 text-white/50">Belum</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {!items.length && (
              <tr><td colSpan={7} className="p-6 text-center text-white/30 text-sm">
                {warning ? "Katalog tidak dapat dimuat dari undeadshopp.com." : "Belum ada data. Tekan “Tarik Katalog”."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 3. Produk instan yang sudah diimport                                */
/* ------------------------------------------------------------------ */
const EMPTY_MANUAL = {
  external_id: "", title: "", cost_price: "", price: "", image_url: "", description: "",
  category: "",
};


// Foto produk dipasang lewat link (bukan upload file), jadi link yang di-paste
// harus benar-benar bisa dirender browser. Cek ringan di sisi klien supaya
// admin langsung tahu sebelum request dikirim; backend tetap memvalidasi ulang.
const isValidImageUrl = (url) => /^https?:\/\/\S+$/i.test(String(url || "").trim());


function ProductsTab() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [manual, setManual] = useState(EMPTY_MANUAL);
  const [adding, setAdding] = useState(false);
  // Hasil "Cek Kode": { valid: true|false|null, message, needs_whitelist, server_ip }
  const [codeCheck, setCodeCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  // Baris yang sedang diedit foto/keterangan/kategorinya:
  // { id, image_url, description, category }
  const [editing, setEditing] = useState(null);
  // Kategori (game) resmi milik admin — produk instan WAJIB memakai salah satu
  // darinya supaya ikut muncul saat pembeli membuka/memfilter kategori itu.
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/instant-products/categories");
        setCategories(data.items || []);
        // Preselect default supaya admin tidak perlu memilih untuk kasus umum.
        const def = (data.items || []).find((c) => c.id === data.default);
        if (def) setManual((m) => (m.category ? m : { ...m, category: def.id }));
      } catch {
        /* dropdown tetap bisa dipakai kosong; backend yang menentukan default */
      }
    })();
  }, []);

  const load = useCallback(async () => {

    setLoading(true);
    try {
      const { data } = await api.get("/admin/instant-products", { params: { q: q || undefined } });
      setItems(data.items || []);
    } catch {
      toast.error("Gagal memuat produk instan");
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/instant-products/${id}`, body);
      toast.success("Produk diperbarui");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memperbarui");
    }
  };

  // Simpan hasil edit foto/keterangan pada baris tabel. String kosong dikirim
  // apa adanya supaya admin bisa MENGHAPUS foto/keterangan, bukan cuma mengganti.
  const saveEdit = async () => {
    if (!editing) return;
    const url = editing.image_url.trim();
    if (url && !isValidImageUrl(url))
      return toast.error("Link foto harus diawali http:// atau https://");
    const body = { image_url: url, description: editing.description.trim() };
    // Kategori hanya dikirim bila admin memilihnya, supaya produk lama yang
    // kategorinya belum ada di daftar tidak ikut tertolak backend.
    if (editing.category) body.category = editing.category;
    await patch(editing.id, body);
    setEditing(null);

  };

  const remove = async (id) => {

    if (!window.confirm("Hapus produk instan ini?")) return;
    try {
      await api.delete(`/admin/instant-products/${id}`);
      toast.success("Produk dihapus");
      load();
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  // Validasi "Kode Produk" undeadshopp sebelum produk disimpan. Kode inilah
  // penghubung produk lokal ⇄ layanan supplier saat pesanan dikirim otomatis.
  const checkCode = async () => {
    const code = manual.external_id.trim();
    if (!code) return toast.error("Isi Kode Produk undeadshopp dulu");
    setChecking(true);
    setCodeCheck(null);
    try {
      const { data } = await api.post("/admin/integrations/undeadshopp/verify-code", { code });
      setCodeCheck(data);
      if (data.valid === true) toast.success(data.message);
      else if (data.valid === false) toast.error(data.message);
      else toast.warning(data.message);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Gagal memeriksa kode";
      setCodeCheck({ valid: null, message: msg });
      toast.error(msg);
    } finally {
      setChecking(false);
    }
  };

  // Fallback saat katalog API undeadshopp error: admin isi External ID (kode
  // layanan di undeadshopp) beserta harganya secara manual.
  const addManual = async () => {
    if (!manual.external_id.trim() || !manual.title.trim())
      return toast.error("External ID dan nama produk wajib diisi");
    if (!Number(manual.price)) return toast.error("Harga jual wajib diisi");
    // Foto opsional, tapi kalau diisi harus berupa link http(s) — kalau tidak,
    // gambar pasti gagal tampil di katalog pembeli.
    if (manual.image_url.trim() && !isValidImageUrl(manual.image_url))
      return toast.error("Link foto harus diawali http:// atau https://");

    // Kode yang sudah terbukti SALAH tidak boleh disimpan diam-diam — produk
    // seperti itu pasti gagal kirim saat pembeli bayar.
    if (codeCheck?.valid === false &&
        !window.confirm(`Kode "${manual.external_id.trim()}" ditolak undeadshopp. Tetap simpan?`))
      return;
    setAdding(true);

    try {
      await api.post("/admin/integrations/undeadshopp/import", {
        items: [{
          external_id: manual.external_id.trim(),
          title: manual.title.trim(),
          cost_price: Number(manual.cost_price) || 0,
          price: Number(manual.price),
          image_url: manual.image_url.trim() || undefined,
          description: manual.description.trim() || undefined,
          category: manual.category || undefined,
        }],


      });
      toast.success("Produk instan ditambahkan");
      setManual(EMPTY_MANUAL);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menambah produk");
    } finally {
      setAdding(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/integrations/undeadshopp/sync");
      toast.success(`Sinkron: ${data.synced} produk, ${data.deactivated.length} dinonaktifkan`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal sinkron");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input className={`${inputCls} pl-9`} placeholder="Cari produk instan…" value={q}
                 onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        <button className={btnGhost} onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Muat
        </button>
        <button className={btnPrimary} onClick={sync} disabled={syncing}>
          <RotateCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> Sinkron Harga & Stok
        </button>
      </div>

      <Card title="Tambah Produk Manual"
            desc="Kode Produk = kode layanan persis seperti di undeadshopp.com. Kode inilah penghubungnya: saat pembeli bayar, server mengirim kode ini ke supplier untuk diproses otomatis.">
        <div className="grid gap-3 md:grid-cols-5">
          <div>
            <label className={labelCls}>Kode Produk undeadshopp</label>
            <input className={inputCls} value={manual.external_id} placeholder="mis. ML86"
                   onChange={(e) => {
                     setManual((m) => ({ ...m, external_id: e.target.value }));
                     setCodeCheck(null); // kode berubah → hasil cek lama tak berlaku
                   }} />
          </div>

          <div className="md:col-span-2">
            <label className={labelCls}>Nama Produk</label>
            <input className={inputCls} value={manual.title} placeholder="Mobile Legends 86 Diamond"
                   onChange={(e) => setManual((m) => ({ ...m, title: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls}>Modal (Rp)</label>
            <input type="number" className={inputCls} value={manual.cost_price}
                   onChange={(e) => setManual((m) => ({ ...m, cost_price: e.target.value }))} />
          </div>
          <div>
            <label className={labelCls}>Harga Jual (Rp)</label>
            <input type="number" className={inputCls} value={manual.price}
                   onChange={(e) => setManual((m) => ({ ...m, price: e.target.value }))} />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Kategori / Game</label>
            <select className={inputCls} value={manual.category}
                    onChange={(e) => setManual((m) => ({ ...m, category: e.target.value }))}>
              <option value="">— Pilih kategori —</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-white/40">
              Menentukan di kategori mana produk ini muncul untuk pembeli (mis. Mobile Legends).
            </p>
          </div>
        </div>


        {/* Foto & keterangan: keduanya opsional, tapi sangat menentukan tampilan
            produk di katalog pembeli. Foto dipasang lewat link agar admin bebas
            memakai gambar resmi game tanpa perlu upload. */}
        <div className="mt-3 grid gap-3 md:grid-cols-5">
          <div className="md:col-span-3">
            <label className={labelCls}>Link Foto Produk (opsional)</label>
            <div className="flex gap-2">
              <input className={inputCls} value={manual.image_url}
                     placeholder="https://i.imgur.com/xxxx.jpg"
                     onChange={(e) => setManual((m) => ({ ...m, image_url: e.target.value }))} />
              {manual.image_url.trim() && (
                <button type="button" title="Hapus link foto"
                        onClick={() => setManual((m) => ({ ...m, image_url: "" }))}
                        className="shrink-0 rounded border border-white/15 px-2 text-white/50 hover:bg-white/5">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <p className="mt-1 text-[11px] text-white/40">
              Salin alamat gambar (klik kanan → “Salin alamat gambar”). Harus diawali
              <span className="font-mono text-[#00F0FF]"> https://</span> dan berakhiran gambar
              (.jpg/.png/.webp).
            </p>
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Pratinjau Foto</label>
            <div className="flex h-[76px] items-center justify-center overflow-hidden rounded border border-white/10 bg-black/40">
              {isValidImageUrl(manual.image_url) ? (
                <img src={manual.image_url.trim()} alt="Pratinjau produk"
                     className="h-full w-full object-contain"
                     onError={(e) => { e.currentTarget.style.display = "none"; }}
                     onLoad={(e) => { e.currentTarget.style.display = "block"; }} />
              ) : (
                <span className="flex items-center gap-2 text-[11px] text-white/30">
                  <ImageIcon className="h-4 w-4" /> Belum ada foto
                </span>
              )}
            </div>
          </div>
          <div className="md:col-span-5">
            <label className={labelCls}>Keterangan Produk (opsional)</label>
            <textarea rows={3} className={inputCls} value={manual.description}
                      placeholder="Contoh: Proses otomatis 1-3 menit. Siapkan User ID & Zone ID sebelum membeli."
                      onChange={(e) => setManual((m) => ({ ...m, description: e.target.value }))} />
            <p className="mt-1 text-[11px] text-white/40">
              Teks ini tampil di halaman detail produk. Kosongkan untuk memakai keterangan default.
            </p>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button className={btnGhost} onClick={checkCode} disabled={checking}>
            <Plug className={`h-4 w-4 ${checking ? "animate-pulse" : ""}`} />
            {checking ? "Mengecek…" : "Cek Kode"}
          </button>
          <button className={btnPrimary} onClick={addManual} disabled={adding}>
            <Download className="h-4 w-4" /> {adding ? "Menyimpan…" : "Tambah Produk"}
          </button>
          {codeCheck && (
            <span
              className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] ${
                codeCheck.valid === true
                  ? "bg-emerald-500/15 text-emerald-400"
                  : codeCheck.valid === false
                  ? "bg-[#FF003C]/15 text-[#FF003C]"
                  : "bg-[#FFD700]/15 text-[#FFD700]"
              }`}
            >
              {codeCheck.valid === true ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : (
                <AlertTriangle className="h-3 w-3" />
              )}
              {codeCheck.message}
            </span>
          )}
        </div>
        <p className="mt-2 text-[11px] text-white/40">
          Tulis kode <span className="font-mono text-[#00F0FF]">persis sama</span> seperti di
          undeadshopp.com (huruf besar/kecil berpengaruh). Kode inilah yang dipakai server untuk
          meneruskan pesanan — salah kode = pengiriman otomatis gagal.
        </p>
      </Card>


      <div className="border border-white/10 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-white/50 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="p-2 w-14 text-left">Foto</th>
              <th className="p-2 text-left">Produk</th>
              <th className="p-2 text-left">External ID</th>

              <th className="p-2 text-right">Modal</th>
              <th className="p-2 text-right">Harga Jual</th>
              <th className="p-2 text-right">Stok</th>
              <th className="p-2 text-center">Status</th>
              <th className="p-2 text-center">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <Fragment key={p.id}>
              <tr className="border-t border-white/5 hover:bg-white/5">

                <td className="p-2">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.title}
                         className="h-10 w-10 rounded border border-white/10 object-cover" />
                  ) : (
                    <div className="flex h-10 w-10 items-center justify-center rounded border border-dashed border-white/15 text-white/25">
                      <ImageIcon className="h-4 w-4" />
                    </div>
                  )}
                </td>
                <td className="p-2 text-white">
                  {p.title}
                  <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#00F0FF]/15 text-[#00F0FF]">INSTAN</span>
                  {p.description && (
                    <p className="mt-0.5 max-w-[280px] truncate text-[11px] text-white/35">{p.description}</p>
                  )}
                </td>

                <td className="p-2 text-white/40 font-mono text-xs">{p.external_id}</td>
                <td className="p-2 text-right text-white/60">{formatIDR(p.cost_price || 0)}</td>
                <td className="p-2 text-right">
                  <input type="number" defaultValue={p.price}
                         className="w-28 bg-black/40 border border-white/10 rounded px-2 py-1 text-right text-[#00F0FF]"
                         onBlur={(e) => {
                           const v = Number(e.target.value);
                           if (v && v !== p.price) patch(p.id, { price: v });
                         }} />
                </td>
                <td className="p-2 text-right text-white/60">{p.stock ?? "∞"}</td>
                <td className="p-2 text-center">
                  <button onClick={() => patch(p.id, { active: !p.active })}
                          className={`text-[10px] px-2 py-1 rounded ${
                            p.active ? "bg-emerald-500/15 text-emerald-400" : "bg-white/10 text-white/50"}`}>
                    {p.active ? "Aktif" : "Nonaktif"}
                  </button>
                </td>
                <td className="p-2 text-center whitespace-nowrap">
                  <button
                    title="Ubah foto, keterangan & kategori"
                    onClick={() =>
                      setEditing((cur) =>
                        cur?.id === p.id
                          ? null
                          : {
                              id: p.id,
                              image_url: p.image_url || "",
                              description: p.description || "",
                              category: p.category || "",
                            })
                    }

                    className="mr-2 text-[#00F0FF] hover:text-white"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(p.id)} className="text-[#FF003C] hover:text-white">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>

              {/* Editor foto & keterangan untuk produk yang sudah tersimpan,
                  termasuk hasil import katalog. */}
              {editing?.id === p.id && (
                <tr className="border-t border-white/5 bg-black/30">
                  <td colSpan={8} className="p-3">
                    <div className="grid gap-3 md:grid-cols-4">
                      <div className="md:col-span-3">
                        <label className={labelCls}>Link Foto Produk</label>
                        <input className={inputCls} value={editing.image_url}
                               placeholder="https://… (kosongkan untuk menghapus foto)"
                               onChange={(e) => setEditing((c) => ({ ...c, image_url: e.target.value }))} />
                      </div>
                      <div>
                        <label className={labelCls}>Pratinjau</label>
                        <div className="flex h-[42px] items-center justify-center overflow-hidden rounded border border-white/10 bg-black/40">
                          {isValidImageUrl(editing.image_url) ? (
                            <img src={editing.image_url.trim()} alt="Pratinjau"
                                 className="h-full w-full object-contain"
                                 onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
                                 onLoad={(e) => { e.currentTarget.style.visibility = "visible"; }} />
                          ) : (
                            <ImageIcon className="h-4 w-4 text-white/25" />
                          )}
                        </div>
                      </div>
                      <div className="md:col-span-2">
                        <label className={labelCls}>Kategori / Game</label>
                        <select className={inputCls} value={editing.category}
                                onChange={(e) => setEditing((c) => ({ ...c, category: e.target.value }))}>
                          <option value="">— Biarkan seperti sekarang —</option>
                          {categories.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="md:col-span-4">
                        <label className={labelCls}>Keterangan Produk</label>
                        <textarea rows={3} className={inputCls} value={editing.description}
                                  placeholder="Keterangan yang tampil di halaman detail produk"
                                  onChange={(e) => setEditing((c) => ({ ...c, description: e.target.value }))} />
                      </div>

                    </div>
                    <div className="mt-3 flex gap-2">
                      <button className={btnPrimary} onClick={saveEdit}>
                        <Save className="h-4 w-4" /> Simpan
                      </button>
                      <button className={btnGhost} onClick={() => setEditing(null)}>
                        <X className="h-4 w-4" /> Batal
                      </button>
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
            {!items.length && (
              <tr><td colSpan={8} className="p-6 text-center text-white/30 text-sm">

                Belum ada produk instan. Import dari tab “Katalog API”.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 4. Antrean pengiriman                                               */
/* ------------------------------------------------------------------ */
/** Modal rincian satu antrean kirim — semua data pesanan dalam satu layar. */
function JobDetailModal({ reference, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: d } = await api.get(
          `/admin/instant-orders/${encodeURIComponent(reference)}/detail`
        );
        if (alive) setData(d);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Gagal memuat detail");
      }
    })();
    return () => { alive = false; };
  }, [reference]);

  const Row = ({ label, value }) =>
    value === undefined || value === null || value === "" ? null : (
      <div className="flex gap-3 py-1 border-b border-white/5 text-sm">
        <span className="w-40 shrink-0 text-white/40">{label}</span>
        <span className="text-white break-all">{String(value)}</span>
      </div>
    );

  const Section = ({ title, children }) => (
    <div className="mb-4">
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{title}</div>
      {children}
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-[#0d0d0f] border border-white/10 rounded-lg w-full max-w-3xl p-5 my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold tracking-wide">
            DETAIL ANTREAN · <span className="font-mono text-xs text-white/50">{reference}</span>
          </h3>
          <button onClick={onClose} className={`${btnGhost} !px-2 !py-1`}>Tutup</button>
        </div>

        {err && <div className="text-red-400 text-sm">{err}</div>}
        {!data && !err && <div className="text-white/40 text-sm">Memuat…</div>}

        {data && (
          <>
            <Section title="Status Pengiriman">
              <Row label="Status" value={data.job?.status} />
              <Row label="Percobaan" value={data.job?.attempts} />
              <Row label="Ref Supplier" value={data.job?.external_ref} />
              <Row label="Kode Produk (API)" value={data.job?.external_id} />
              <Row label="Dibuat" value={data.job?.created_at} />
              <Row label="Terkirim" value={data.job?.delivered_at} />
              <Row label="Pesan Error" value={data.job?.error} />
            </Section>

            <Section title="Pesanan">
              <Row label="Order ID" value={data.order?.id} />
              <Row label="Merchant Ref" value={data.order?.merchant_ref} />
              <Row label="Status Order" value={data.order?.status} />
              <Row label="Status Bayar" value={data.order?.payment_status} />
              <Row label="Metode Bayar" value={data.order?.payment_method} />
              <Row label="Total" value={data.order?.total} />
              <Row label="Waktu Order" value={data.order?.created_at} />
            </Section>

            <Section title="Pembeli">
              <Row label="Nama" value={data.buyer?.name} />
              <Row label="Email" value={data.buyer?.email} />
              <Row label="No. HP" value={data.buyer?.phone} />
            </Section>

            <Section title="Produk & Data Isian">
              <Row label="Produk" value={data.item?.title || data.product?.title} />
              <Row label="Jumlah" value={data.item?.quantity} />
              <Row label="Harga" value={data.item?.price} />
              {Object.entries(data.item?.order_fields || {}).map(([k, v]) => (
                <Row key={k} label={k} value={v} />
              ))}
            </Section>

            {!!(data.item?.delivered_codes || []).length && (
              <Section title="Kode / Akun Terkirim">
                <pre className="bg-black/50 border border-white/10 rounded p-3 text-xs text-emerald-300 whitespace-pre-wrap break-all">
                  {(data.item.delivered_codes || []).map((c) => c.code).join("\n")}
                </pre>
              </Section>
            )}

            {data.job?.raw_response && (
              <Section title="Balasan Mentah Supplier">
                <pre className="bg-black/50 border border-white/10 rounded p-3 text-xs text-white/50 whitespace-pre-wrap break-all max-h-48 overflow-y-auto">
                  {typeof data.job.raw_response === "string"
                    ? data.job.raw_response
                    : JSON.stringify(data.job.raw_response, null, 2)}
                </pre>
              </Section>
            )}

            {!!(data.logs || []).length && (
              <Section title="Riwayat Log">
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {data.logs.map((l, i) => (
                    <div key={i} className="text-xs font-mono flex gap-2">
                      <span className={l.ok ? "text-emerald-400" : "text-red-400"}>
                        {l.ok ? "OK " : "ERR"}
                      </span>
                      <span className="text-white/40">{l.created_at}</span>
                      <span className="text-white/60">{l.action}</span>
                      <span className="text-white/40 break-all">{l.detail}</span>
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function JobsTab() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [detailRef, setDetailRef] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/instant-orders", {
        params: { status: status || undefined, limit: 100 },
      });
      setItems(data.items || []);
    } catch {
      toast.error("Gagal memuat antrean");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const retry = async (ref) => {
    try {
      const { data } = await api.post(`/admin/instant-orders/${encodeURIComponent(ref)}/retry`);
      data.ok ? toast.success("Berhasil dikirim") : toast.info(`Status: ${data.status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry gagal");
    }
  };

  const manual = async (ref) => {
    const code = window.prompt("Masukkan kode/akun yang dikirim manual (pisahkan koma):");
    if (!code) return;
    try {
      await api.post(`/admin/instant-orders/${encodeURIComponent(ref)}/manual-deliver`, {
        codes: code.split(",").map((c) => c.trim()).filter(Boolean),
      });
      toast.success("Kode terkirim ke pembeli");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal kirim manual");
    }
  };

  const retryAll = async () => {
    try {
      const { data } = await api.post("/admin/instant-orders/retry-all");
      toast.success(`Dicek ${data.checked}, terkirim ${data.delivered}, gagal ${data.failed}`);
      load();
    } catch {
      toast.error("Gagal memproses antrean");
    }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <select className={`${inputCls} max-w-[200px]`} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Semua Status</option>
          <option value="pending">Pending</option>
          <option value="success">Terkirim</option>
          <option value="failed">Gagal</option>
        </select>
        <button className={btnGhost} onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Muat
        </button>
        <button className={btnPrimary} onClick={retryAll}>
          <RotateCw className="w-4 h-4" /> Proses Semua Pending
        </button>
      </div>

      <div className="border border-white/10 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-white/50 text-[10px] uppercase tracking-widest">
            <tr>
              <th className="p-2 text-left">Referensi</th>
              <th className="p-2 text-left">Produk</th>
              <th className="p-2 text-right">Qty</th>
              <th className="p-2 text-center">Percobaan</th>
              <th className="p-2 text-center">Status</th>
              <th className="p-2 text-left">Keterangan</th>
              <th className="p-2 text-center">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.map((j) => {
              const b = JOB_BADGE[j.status] || JOB_BADGE.pending;
              return (
                <tr key={j.reference} className="border-t border-white/5 hover:bg-white/5">
                  <td className="p-2 font-mono text-xs text-white/50">{j.reference}</td>
                  <td className="p-2 text-white">{j.product_title || j.external_id}</td>
                  <td className="p-2 text-right text-white/60">{j.quantity}</td>
                  <td className="p-2 text-center text-white/60">{j.attempts || 0}</td>
                  <td className="p-2 text-center">
                    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded ${b.cls}`}>
                      <b.Icon className="w-3 h-3" /> {b.label}
                    </span>
                  </td>
                  <td className="p-2 text-white/40 text-xs max-w-[240px] truncate">{j.error || j.external_ref || "-"}</td>
                  <td className="p-2 text-center whitespace-nowrap">
                    <button
                      onClick={() => setDetailRef(j.reference)}
                      className={`${btnGhost} mr-1 !px-2 !py-1`}
                      title="Lihat rincian pesanan"
                    >
                      <Search className="w-3 h-3" /> Detail
                    </button>
                    {j.status !== "success" && (
                      <>
                        <button onClick={() => retry(j.reference)} className={`${btnGhost} mr-1 !px-2 !py-1`}>
                          <RotateCw className="w-3 h-3" /> Retry
                        </button>
                        <button onClick={() => manual(j.reference)} className={`${btnGhost} !px-2 !py-1`}>
                          <Send className="w-3 h-3" /> Manual
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {!items.length && (
              <tr><td colSpan={7} className="p-6 text-center text-white/30 text-sm">
                Belum ada antrean pengiriman.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {detailRef && (
        <JobDetailModal reference={detailRef} onClose={() => setDetailRef(null)} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
export default function AdminInstantProducts() {
  const [sub, setSub] = useState("connection");

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-bold text-white uppercase tracking-widest flex items-center gap-2">
          <Zap className="w-5 h-5 text-[#FFD700]" /> Produk Instan · undeadshopp.com
        </h2>
        <p className="text-xs text-white/40 mt-1">
          Produk milik admin yang dikirim otomatis oleh API undeadshopp.com begitu pembayaran lunas.
        </p>
      </div>

      <div className="flex gap-1 mb-4 border-b border-white/10">
        {SUB_TABS.map((t) => (
          <button key={t.id} onClick={() => setSub(t.id)}
                  data-testid={`instant-subtab-${t.id}`}
                  className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase tracking-widest border-b-2 ${
                    sub === t.id ? "border-[#FF003C] text-[#FF003C]" : "border-transparent text-white/50 hover:text-white"
                  }`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {sub === "connection" && <ConnectionTab />}
      {sub === "catalog" && <CatalogTab />}
      {sub === "products" && <ProductsTab />}
      {sub === "jobs" && <JobsTab />}
    </div>
  );
}
